import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Outlet, useSearchParams } from "react-router-dom";
import "./CarrierProfile.css";
import { Box, CircularProgress } from "@mui/material";
import CarrierProfileGeneral from "../CarrierProfile/CarrierProfileGeneral/CarrierProfileGeneral";
import PrimaryTabPanel from "../TabPanels/PrimaryTabPanel/PrimaryTabPanel";
import PrimaryTabs from "../TabPanels/PrimaryTabPanel/PrimaryTabs/PrimaryTabs";
import CarrierFreightMatches from "./CarrierFreightMatches/CarrierFreightMatches";
import CarrierProfilePostedTrucks from "./CarrierProfilePostedTrucks/CarrierProfilePostedTrucks";
import CarrierProfilePreferredLanes from "./CarrierProfilePreferredLanes/CarrierProfilePreferredLanes";
import CarrierProfileHistoricalLoads from "./CarrierProfileHistoricalLoads/CarrierProfileHistoricalLoads";
import CarrierProfileAttributesContainer from "./CarrierProfileAttributesContainer/CarrierProfileAttributesContainer";
import CarrierProfileContacts from "./CarrierProfileContacts/CarrierProfileContacts";
import useFreightMatchReceiver from "../../hooks/useFreightMatchReceiver/useFreightMatchReceiver";
import { CarrierService } from "../../services/CarrierService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";

export default function CarrierProfile({
  refreshPage,
  setRefreshPage,
  handleTableAction,
  handleRecentlyViewed
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  let [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [carrierId, setCarrierId] = useState("");
  const [activeCarrier, setActiveCarrier] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [freightMatches, setFreightMatches] = useState([]);
  const [newLoadsCount, setNewLoadsCount] = useState(0);
  const [radiusOrigin, setRadiusOrigin] = useState("50");
  const [radiusDest, setRadiusDest] = useState("50");
  const [matchesLoading, setMatchesLoading] = useState(false);
  const {
    newCarrierMatches,
    setNewCarrierMatches
  } = useFreightMatchReceiver("carrier");

  const radiusOptions = [
    {
      name: "25 mi",
      value: "25"
    },
    {
      name: "50 mi",
      value: "50"
    },
    {
      name: "100 mi",
      value: "100"
    },
    {
      name: "150 mi",
      value: "150"
    },
    {
      name: "200 mi",
      value: "200"
    },
    {
      name: "250 mi",
      value: "250"
    },
    {
      name: "300 mi",
      value: "300"
    },
    {
      name: "350 mi",
      value: "350"
    },
    {
      name: "400 mi",
      value: "400"
    },
    {
      name: "450 mi",
      value: "450"
    },
    {
      name: "500 mi",
      value: "500"
    }
  ];

  const tabLabels = [
    {
      name: "Matches"
    },
    {
      name: "Posted Trucks"
    },
    {
      name: "Preferred Lanes"
    },
    {
      name: "Load History"
    },
    {
      name: "Attributes"
    },
    {
      name: "Contacts"
    }
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    searchParams.set("tab", newValue);
    window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
  };

  const handleGetMatches = (manualRefresh = false) => {
    if (activeCarrier?.id) {
      const searchParams = {
        userId: activeUser?.userIdMcld,
        carrierId: activeCarrier.id,
        originSearchMiles: radiusOrigin,
        destinationSearchMiles: radiusDest
      };

      setMatchesLoading(true);
      CarrierService.GetCarrierFreightMatches(instance, searchParams).then(res => {
        let filteredLoads = res.loads.filter(({ removedFlag }) => !removedFlag);
        filteredLoads.forEach(load => { load.newlyAdded = false; });
        setNewLoadsCount(0);

        if (newCarrierMatches && !manualRefresh) {
          let oldLoads = filteredLoads.filter(load => freightMatches.some(({ orderId, carrierId }) => orderId === load.orderId && carrierId === load.carrierId));
          let newLoads = filteredLoads.filter(load => !freightMatches.some(({ orderId, carrierId }) => orderId === load.orderId && carrierId === load.carrierId));
          newLoads.forEach(load => { load.newlyAdded = true; });
          setNewLoadsCount(newLoads.length);

          filteredLoads = newLoads.concat(oldLoads);
        }

        setFreightMatches(filteredLoads);
        setMatchesLoading(false);
      }).catch(error => {
        console.error(error);
        setMatchesLoading(false);
        warning(error);
      });
    }
  };

  const getCarrier = (initialLoad) => {
    if (carrierId?.length) {
      if (initialLoad) {
        setIsLoading(true);
      }

      CarrierService.GetCarrier(instance, carrierId).then(res => {
        const item = {
          type: "carrier",
          carrierId: carrierId,
          phoneNumber: res.phoneNumber,
          name: res.name,
          isBookmark: false
        };
        handleRecentlyViewed(item);

        setActiveCarrier(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  useEffect(() => {
    if (refreshPage) {
      getCarrier();
      setRefreshPage(false);
    }
  }, [refreshPage]);

  useEffect(() => {
    if (carrierId) {
      getCarrier(true);
    }
  }, [carrierId]);

  useEffect(() => {
    setCarrierId(searchParams.get("carrierId"));

    const tab = searchParams.get("tab");
    if (tab) {
      setTabValue(parseInt(tab));
    } else {
      setTabValue(0);
    }
  }, [searchParams]);

  useEffect(() => {
    handleGetMatches();
  }, [activeCarrier, radiusOrigin, radiusDest]);

  useEffect(() => {
    if (newCarrierMatches) {
      handleGetMatches();
      setNewCarrierMatches(false);
    }
  }, [newCarrierMatches]);

  return (
    <Box sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={50} />
        </Box>
      ) : (
        <Box sx={{ backgroundColor: "white_opacity.100" }}>
          <Box
            sx={{
              position: "sticky",
              zIndex: 999,
              backgroundColor: "white_opacity.100",
              width: "100%"
            }}
          >
            <CarrierProfileGeneral carrier={activeCarrier} getCarrier={getCarrier} setIsUpdating={setIsUpdating} />
            <Box sx={{ borderBottom: 1, borderColor: "divider", padding: "0 24px", marginBottom: "10px", backgroundColor: "black_solid.05" }}>
              <PrimaryTabs value={tabValue} handleChange={handleTabChange} tabLabels={tabLabels} />
            </Box>
          </Box>
          <PrimaryTabPanel value={tabValue} index={0}>
            <CarrierFreightMatches
              isLoading={matchesLoading}
              activeCarrier={activeCarrier}
              freightMatches={freightMatches}
              setFreightMatches={setFreightMatches}
              newLoadsCount={newLoadsCount}
              radiusOptions={radiusOptions}
              radiusOrigin={radiusOrigin}
              setRadiusOrigin={setRadiusOrigin}
              radiusDest={radiusDest}
              setRadiusDest={setRadiusDest}
              handleGetMatches={handleGetMatches}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={1}>
            <CarrierProfilePostedTrucks
              activeUser={activeUser}
              activeCarrier={activeCarrier}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={2}>
            <CarrierProfilePreferredLanes
              activeUser={activeUser}
              activeCarrier={activeCarrier}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={3}>
            <CarrierProfileHistoricalLoads
              activeCarrier={activeCarrier}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={4}>
            <CarrierProfileAttributesContainer
              activeCarrier={activeCarrier}
              isUpdating={isUpdating}
              setIsUpdating={setIsUpdating}
              setActiveCarrier={setActiveCarrier}
            />
          </PrimaryTabPanel>
          <PrimaryTabPanel value={tabValue} index={5}>
            <CarrierProfileContacts
              activeCarrier={activeCarrier}
              handleTableAction={handleTableAction}
            />
          </PrimaryTabPanel>
        </Box>
      )}
      <Outlet />
    </Box>
  );
}

CarrierProfile.propTypes = {
  refreshPage: PropTypes.bool,
  setRefreshPage: PropTypes.func,
  handleTableAction: PropTypes.func,
  handleRecentlyViewed: PropTypes.func.isRequired
};